import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import {
  Subscription,
  switchMap,
  combineLatest,
  BehaviorSubject,
  distinctUntilChanged,
} from 'rxjs';
import { Store } from '@ngrx/store';
import { Provider, ProviderSearch, LastSearchPerformed } from '../../../models';
import {
  setProvidersSelectedBenefit,
  setProvidersResultsDataByBenefit,
  setSelectedProviderDetails,
  setProvidersLastSearchPerformed,
  setResetResultsByBenefit,
} from '../../../state/providers/providers.actions';
import {
  selectProvidersResultsByBenefit,
  selectProvidersSearchOptions,
  selectSelectedProviderDetails,
  selectProvidersSelectedBenefit,
  selectProvidersLastSearchPerformed,
} from '../../../state/providers/providers.selectors';
import { hideLoading, showLoading } from '../../../state/app/app.actions';
import { ProviderService } from '../../../services/provider.service';
import { StringUtils } from '../../../shared/utils/utils';

@Component({
  selector: 'app-provider-list',
  templateUrl: './provider-list.component.html',
  styleUrl: './provider-list.component.scss',
})
export class ProviderListComponent implements OnInit {
  private subscription = new Subscription();
  private userInteracted = false;
  @ViewChild('paginator') paginator: MatPaginator;
  matDataSource: MatTableDataSource<Provider> = new MatTableDataSource();
  providerSearch: ProviderSearch = new ProviderSearch();
  providerSearchParam: ProviderSearch = new ProviderSearch();

  updateResultsByProvince$ = new BehaviorSubject<string>('all');
  updateResultsByProfession$ = new BehaviorSubject<string>('all');
  paginatorState$ = new BehaviorSubject<any>({});

  loading: boolean = false;
  providers: Provider[] = [];

  openDetailsByID?: number = null;
  selectedProvider?: Provider = null;
  selectedBenefitTabIndex: number = 0;
  selectedBenefit?: string = null;
  selectedProvince?: string = null;
  selectedProfession?: string = null;
  selectedName?: string = null;

  benefitSearchGroupsSelected: {
    province: string;
    professions: string[];
  }[];
  filteredProfessions: string[] = [];
  filteredProvinces: string[] = [];

  selectedRowIndex: number = 0;
  displayedColumns: string[] = ['results'];
  showConcernsOnly = false;

  resultsMessage: string = '';
  initalSearchMessage: string = 'Begin a search';
  noResultsMessage: string = 'No results found';
  noResultsWithConcerns: string = `The result doesn't contain items with concerns`;

  initialPageIndex: number = 0;
  initialPageSize: number = 20;
  initialTotalElements: number = 0;

  lastSearchPerformed?: LastSearchPerformed = null;
  hasValidLastSearchPerformed: boolean = false;
  isServerSearch: boolean = false;
  isSearchFromDisciplines: boolean = false;
  defaultBenefits: string[] = ['Pharmacy'];

  constructor(
    private providerService: ProviderService,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store
  ) {
    this.providerSearchParam.benefits = this.defaultBenefits;
    this.resultsMessage = this.initalSearchMessage;
  }

  ngOnInit(): void {
    const getPreloadDataSub = combineLatest([
      this.store
        .select(selectProvidersSelectedBenefit)
        .pipe(distinctUntilChanged()),
      this.updateResultsByProvince$.pipe(distinctUntilChanged()),
      this.updateResultsByProfession$.pipe(distinctUntilChanged()),
    ])
      .pipe(
        switchMap(([benefit, province, profession]) => {
          this.selectedBenefit = benefit;
          this.resultsMessage = this.initalSearchMessage;

          return this.store.select(
            selectProvidersResultsByBenefit(
              benefit,
              province || 'all',
              profession || 'all'
            )
          );
        }),
        distinctUntilChanged((prev, curr) => {
          const isSameContentThanPrevious =
            JSON.stringify(prev?.content) === JSON.stringify(curr?.content);
          const isViewSameLenghtAsResults =
            this.providers.length === curr?.content?.length;

          return isSameContentThanPrevious && isViewSameLenghtAsResults;
        })
      )
      .subscribe((data) => {
        this.processResults(data);
      });

    const searchOptionsByBenefitSub = this.store
      .select(selectProvidersSearchOptions)
      .subscribe((searchOptions) => {
        if (searchOptions?.benefitSearchGroups?.length > 0) {
          this.providerSearchParam = {
            ...searchOptions,
            benefits: searchOptions?.benefitSearchGroups.map(
              (benefitGroup) => benefitGroup.benefit
            ),
          };

          // perform a benefit sync if not already selected
          if (!this.selectedBenefit) {
            this.selectedBenefit =
              searchOptions?.benefitSearchGroups[0].benefit;
            this.setRoute();
            this.store.dispatch(
              setProvidersSelectedBenefit({
                providersSelectedBenefit: this.selectedBenefit,
              })
            );
          }
          setTimeout(() => {
            this.selectedBenefitTabIndex =
              this.providerSearchParam.benefits.indexOf(this.selectedBenefit);
          });
          this.updateFiltersByBenefitSelected();
        }
      });

    const selectedProviderSub = this.store
      .select(selectSelectedProviderDetails)
      .pipe(distinctUntilChanged((prev, curr) => prev?.id === curr?.id))
      .subscribe((provider) => {
        this.selectedProvider = provider;
        this.scrollToSelectedRow(provider);
      });

    const lastSearchPerformedSub = this.store
      .select(selectProvidersLastSearchPerformed)
      .pipe(distinctUntilChanged())
      .subscribe((lastSearchPerformed) => {
        this.lastSearchPerformed = lastSearchPerformed;
        this.hasValidLastSearchPerformed =
          !!this.lastSearchPerformed?.queryParams?.ben;
      });

    this.subscription.add(getPreloadDataSub);
    this.subscription.add(searchOptionsByBenefitSub);
    this.subscription.add(selectedProviderSub);
    this.subscription.add(lastSearchPerformedSub);
    this.subscription.add(this.updateResultsByProvince$);
    this.subscription.add(this.updateResultsByProfession$);
  }

  ngAfterViewInit() {
    this.paginatorState$.subscribe((data) => {
      if (this.paginator) {
        this.paginator.length = data.length;
        this.paginator.pageIndex = data.pageIndex;
      }
    });
    this.loadByQueryParamsOrLastPerformed();
  }

  private processResults(data: any): void {
    this.providers = [];
    this.matDataSource.data = [];
    this.paginatorState$.next({
      length: 0,
      pageIndex: 0,
    });

    if (this.hasMinimumSearchQueryParams) {
      if (data?.content?.length == 0 && this.isServerSearch) {
        this.resultsMessage = this.noResultsMessage;
        if (!!this.selectedProvider?.id) {
          this.onCloseDetails();
        }
      } else {
        this.setRoute();
      }
      this.providers = data.content || [];
      this.matDataSource.data = data.content;
      this.showConcernsOnly = this.isSearchFromOtherPage
        ? false
        : data.concerns;

      this.paginatorState$.next({
        length: data.length,
        pageIndex: data.pageIndex,
      });
      this.autoSelectItemDetails();
    }
  }

  private loadByQueryParamsOrLastPerformed() {
    this.isSearchFromDisciplines = history.state.searchFromDisciplines;
    this.openDetailsByID = Number(this.route.snapshot.queryParamMap.get('id'));
    const benefit =
      this.route.snapshot.queryParamMap.get('ben') ||
      this.lastSearchPerformed?.queryParams?.ben;
    this.selectedProvince =
      this.route.snapshot.queryParamMap.get('prov') ||
      this.lastSearchPerformed?.queryParams?.prov;
    this.selectedProfession = this.isSearchFromOtherPage
      ? null
      : this.route.snapshot.queryParamMap.get('prof') ||
        this.lastSearchPerformed?.queryParams?.prof;
    this.selectedName =
      this.route.snapshot.queryParamMap.get('n') ||
      this.lastSearchPerformed?.queryParams?.n;

    if (!this.selectedBenefit || (benefit && benefit != this.selectedBenefit)) {
      const providersSelectedBenefit = benefit
        ? benefit
        : this.providerSearchParam.benefits[0];

      this.store.dispatch(
        setProvidersSelectedBenefit({
          providersSelectedBenefit,
        })
      );
    }

    const isDataInQueryParams =
      !!this.route.snapshot.queryParamMap.get('ben') ||
      !!this.route.snapshot.queryParamMap.get('prov') ||
      !!this.route.snapshot.queryParamMap.get('prof') ||
      !!this.route.snapshot.queryParamMap.get('n');

    if (
      isDataInQueryParams ||
      this.openDetailsByID ||
      this.isSearchFromOtherPage
    ) {
      this.searchPaginatedProviders(true);
    } else {
      this.setRoute();
      if (this.selectedProvince) {
        this.onProvinceSelected(this.selectedProvince);
      }
      if (this.selectedProfession) {
        this.onProfessionSelected(this.selectedProfession);
      }
    }
  }

  setSearchParams(): void {
    this.providerSearch.id = null;
    if (this.openDetailsByID) {
      this.providerSearch.id = this.openDetailsByID;
    }

    if (!this.selectedBenefit) {
      this.selectedBenefit =
        this.providerSearchParam.benefits[this.selectedBenefitTabIndex];
    }

    this.providerSearch.benefits = [this.selectedBenefit];
    this.providerSearch.provinces = [this.selectedProvince];
    this.providerSearch.professions = [this.selectedProfession];
    this.providerSearch.name = this.selectedName?.trim()?.replace(/\s+/g, ' ');
    this.providerSearch.concerns = this.showConcernsOnly ? 'Y' : 'N';

    this.providerSearch.pageSize =
      this.paginator?.pageSize || this.initialPageSize;
    this.providerSearch.pageNumber =
      this.paginator?.pageIndex || this.initialPageIndex;
  }

  searchPaginatedProviders(
    isInitialSearch: boolean = false,
    triggerFromUI: boolean = false
  ): void {
    if (this.hasMinimumSearchQueryParams) {
      if (isInitialSearch) {
        this.loading = true;
        this.store.dispatch(showLoading());
        if (this.paginator) {
          this.paginator.pageIndex = 0;
        }
      }

      if (triggerFromUI) {
        this.resetControlFlags();
      }

      let cacheConcernsAtSetParams = this.showConcernsOnly;
      this.setSearchParams();

      this.providerService
        .searchPaginatedProviders(this.providerSearch)
        .subscribe((providersResults: ProviderSearch) => {
          const content = providersResults?.pageResult?.content;
          this.isServerSearch = true;

          this.store.dispatch(
            setProvidersResultsDataByBenefit({
              benefit: this.selectedBenefit,
              province: this.selectedProvince || 'all',
              profession: this.selectedProfession || 'all',
              providersResultsData: content,
              length: providersResults?.pageResult?.totalElements,
              pageIndex: this.paginator.pageIndex,
              concerns: cacheConcernsAtSetParams,
            })
          );

          this.updateResultsByProvince$.next(this.selectedProvince);
          this.updateResultsByProfession$.next(this.selectedProfession);
          this.setRoute();

          if (isInitialSearch) {
            this.store.dispatch(hideLoading());
            this.loading = false;
            this.resultsMessage = this.noResultsMessage;
          }
        });
    }
  }

  private autoSelectItemDetails() {
    const initialSelectedItem = this.selectedProvider;

    let newSelectedItem = null;

    if (this.openDetailsByID && this.providers.length > 0) {
      newSelectedItem =
        this.providers.find((p) => p.id === this.openDetailsByID) ??
        this.providers[0];
    } else if (this.isSearchFromDisciplines && this.providers.length > 0) {
      newSelectedItem = this.providers[0];
    } else if (
      initialSelectedItem ||
      !!this.lastSearchPerformed?.selectedItem?.id
    ) {
      newSelectedItem = !!this.lastSearchPerformed?.selectedItem?.id
        ? this.lastSearchPerformed?.selectedItem
        : this.providers[0];
    } else {
      newSelectedItem = null;
    }

    if (initialSelectedItem?.id != newSelectedItem?.id) {
      this.dispatchSelectedItem(newSelectedItem);
    }
  }

  clearName() {
    this.selectedName = '';
    if (this.openDetailsByID) {
      this.openDetailsByID = null;
    }
  }

  clear() {
    this.providerSearch = new ProviderSearch();
    this.openDetailsByID = null;
    this.selectedBenefit = null;
    this.selectedName = null;
    this.selectedProvince = null;
    this.selectedProfession = null;
    this.resultsMessage = this.initalSearchMessage;
    this.providers = [];
    this.paginator.pageIndex = this.initialPageIndex;
    this.paginator.pageSize = this.initialPageSize;
    this.paginator.length = this.initialTotalElements;
    this.matDataSource.data = [];
    this.showConcernsOnly = false;
    this.resetControlFlags();
    this.setRoute();
    this.resetResultsState();
  }

  private resetResultsState() {
    this.store.dispatch(setResetResultsByBenefit());
  }

  private setRoute() {
    const queryParams = {
      ben: this.selectedBenefit,
      prov: this.selectedProvince,
      prof: this.selectedProfession,
      n: this.selectedName || undefined,
      id: this.openDetailsByID || undefined,
    };

    this.store.dispatch(
      setProvidersLastSearchPerformed({
        lastSearchPerformed: {
          queryParams,
        },
      })
    );

    this.router.navigate([], {
      queryParams,
    });
  }

  onBenefitTabIndexChange(index: number): void {
    const newBenefit = this.providerSearchParam.benefits[index];
    if (this.selectedBenefit !== newBenefit) {
      this.resetControlFlags();
      this.store.dispatch(
        setProvidersSelectedBenefit({
          providersSelectedBenefit: newBenefit,
        })
      );
      this.updateFiltersByBenefitSelected();
    }
  }

  onUserInteractChange(): void {
    this.userInteracted = true;
  }

  onProvinceSelected(value: string): void {
    if (this.userInteracted) {
      this.resetControlFlags();
      this.selectedProfession = null;
      this.updateResultsByProfession$.next(null);
    }

    this.filteredProfessions =
      this.benefitSearchGroupsSelected?.find((prov) => prov.province === value)
        ?.professions || [];

    this.updateResultsByProvince$.next(value);
  }

  private resetControlFlags() {
    this.userInteracted = false;
    this.isServerSearch = false;
    this.isSearchFromDisciplines = false;
    this.onCloseDetails();
  }

  onProfessionSelected(value: string): void {
    if (this.userInteracted) {
      this.resetControlFlags();
    }
    this.updateResultsByProfession$.next(value);
  }

  openDetails(provider: Provider, index?: number): void {
    if (index || index == 0) {
      this.selectedRowIndex = this.getActualIndex(index);
    }

    this.scrollToSelectedRow(provider);
    this.dispatchSelectedItem(provider);
  }

  private dispatchSelectedItem(provider) {
    this.store.dispatch(
      setSelectedProviderDetails({
        selectedProviderDetails: provider,
      })
    );
    this.store.dispatch(
      setProvidersLastSearchPerformed({
        lastSearchPerformed: {
          selectedItem: provider,
        },
      })
    );
  }

  scrollToSelectedRow(selectedItem): void {
    if (selectedItem) {
      setTimeout(() => {
        const rowElement = document.getElementById(`row-${selectedItem?.id}`);
        if (rowElement) {
          rowElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  }

  onCloseDetails(): void {
    this.store.dispatch(
      setSelectedProviderDetails({
        selectedProviderDetails: null,
      })
    );
    this.store.dispatch(
      setProvidersLastSearchPerformed({
        lastSearchPerformed: {
          selectedItem: null,
        },
      })
    );
  }

  applyFilter(event?: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.matDataSource.filter = filterValue.trim().toLowerCase();

    if (this.matDataSource.paginator) {
      this.matDataSource.paginator.firstPage();
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.matDataSource.data.length === 0) return;

    const getMaxIndexOnCurrentPage = (): number => {
      const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
      const totalRecords = this.paginator.length;
      const endIndex = Math.min(
        startIndex + this.paginator.pageSize - 1,
        totalRecords - 1
      );
      return endIndex;
    };

    const performUpdateInStore = (selectedRowIndex: number) => {
      const relativeIndex = selectedRowIndex % this.paginator.pageSize;
      const newSelectedProvider = this.matDataSource.data[relativeIndex];
      this.dispatchSelectedItem(newSelectedProvider);
    };

    const currentStartIndex =
      this.paginator.pageIndex * this.paginator.pageSize;
    const maxIndex = getMaxIndexOnCurrentPage();

    if (event.key === 'ArrowDown') {
      if (this.selectedRowIndex < maxIndex) {
        this.selectedRowIndex++;
        performUpdateInStore(this.selectedRowIndex);
      }
    } else if (event.key === 'ArrowUp') {
      if (this.selectedRowIndex > currentStartIndex) {
        this.selectedRowIndex--;
        performUpdateInStore(this.selectedRowIndex);
      }
    }
  }

  private getActualIndex(index: number): number {
    return this.paginator.pageIndex * this.paginator.pageSize + index;
  }

  private updateFiltersByBenefitSelected(): void {
    const benefitSearchGroupsSelected =
      this.providerSearchParam.benefitSearchGroups?.filter(
        (benefitGroup) => benefitGroup.benefit === this.selectedBenefit
      )[0]?.provinces || [];
    this.filteredProvinces =
      benefitSearchGroupsSelected?.map((prov) => prov?.province) || [];
    this.benefitSearchGroupsSelected = benefitSearchGroupsSelected;

    if (this.selectedProvince) {
      this.onProvinceSelected(this.selectedProvince);
    }
  }

  getAddress(provider: Provider) {
    const { address, address1, address2, address3, address4 } = provider;
    if (address) return address;

    const combinedAddress = [address1, address2, address3, address4]
      .filter((part) => !!part)
      .join(' ');

    return combinedAddress.length > 0 ? combinedAddress : null;
  }

  formatUrl(url: string): string {
    return StringUtils.formatUrl(url);
  }

  get hasMinimumSearchQueryParams(): boolean {
    return !!this.selectedProvince;
  }

  get isSearchFromOtherPage(): boolean {
    return this.isSearchFromDisciplines || !!this.openDetailsByID;
  }

  exportProviders() {
    const totalResults = this.paginator?.length;
    this.setSearchParams();
    this.loading = true;
    this.store.dispatch(showLoading());
    this.providerService
      .exportProviders({
        ...this.providerSearch,
        professions: this.selectedProfession ? [this.selectedProfession] : null,
        pageNumber: 0,
        pageSize: totalResults,
      })
      .subscribe(() => {
        this.loading = false;
        this.store.dispatch(hideLoading());
      });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.store.dispatch(
      setSelectedProviderDetails({
        selectedProviderDetails: null,
      })
    );
  }
}
